<script setup>
/**
 * The one dialog behind every AI button the owner presses (P-5.12): the price, her balance,
 * a tick, Confirm and Cancel. Cancel costs nothing. Every number comes from `controller`
 * (`useAiConfirm`), which got it from the server; nothing here writes one.
 *
 * In a visit session (Test it, meaning search; O-11) it uses the session wording, because
 * that one tick covers every press of the visit, and says so.
 */
import { computed, ref, watch } from 'vue';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** What `useAiConfirm` returns. */
  controller: { type: Object, required: true },
});

const { t } = useWasloI18n();
const dialogRef = ref(null);
const state = computed(() => props.controller.dialog);

/**
 * The visit-session wording per op (O-11); an op outside it gets the per-action wording.
 * Each string is a literal `t` call, so the i18n lint and a key search see all of them.
 */
const SESSION_COPY = {
  test_message: {
    body: values => t('WASLO.AI_CONFIRM.SESSION.TEST_MESSAGE_BODY', values),
    tick: values => t('WASLO.AI_CONFIRM.SESSION.TEST_MESSAGE_TICK', values),
  },
  search_by_meaning: {
    body: values =>
      t('WASLO.AI_CONFIRM.SESSION.SEARCH_BY_MEANING_BODY', values),
    tick: values =>
      t('WASLO.AI_CONFIRM.SESSION.SEARCH_BY_MEANING_TICK', values),
  },
};

const sessionCopy = computed(() =>
  props.controller.session ? (SESSION_COPY[props.controller.op] ?? null) : null
);

const phrase = value =>
  value === null || value === undefined
    ? ''
    : props.controller.creditsText(value);

const body = computed(() => {
  const values = {
    credits: phrase(state.value.credits),
    balance: phrase(state.value.balance),
  };
  return sessionCopy.value
    ? sessionCopy.value.body(values)
    : t('WASLO.AI_CONFIRM.BODY', values);
});

const tick = computed(() =>
  sessionCopy.value
    ? sessionCopy.value.tick({ credits: phrase(state.value.credits) })
    : t('WASLO.AI_CONFIRM.TICK')
);

const changed = computed(() =>
  state.value.priceChanged
    ? t('WASLO.AI_CONFIRM.PRICE_CHANGED', {
        credits: phrase(state.value.credits),
      })
    : ''
);

watch(
  () => state.value.open,
  open => {
    if (open) dialogRef.value?.open();
    else dialogRef.value?.close();
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('WASLO.AI_CONFIRM.TITLE')"
    :confirm-button-label="t('WASLO.AI_CONFIRM.CONFIRM')"
    :cancel-button-label="t('WASLO.AI_CONFIRM.CANCEL')"
    :disable-confirm-button="!state.ticked"
    data-waslo-ai-confirm
    @confirm="controller.confirm()"
    @close="controller.cancel()"
  >
    <div class="flex flex-col gap-4">
      <p
        v-if="changed"
        class="mb-0 text-sm font-medium text-n-slate-12"
        data-waslo-ai-confirm-changed
      >
        {{ changed }}
      </p>
      <p class="mb-0 text-sm text-n-slate-12" data-waslo-ai-confirm-body>
        {{ body }}
      </p>
      <label
        class="flex items-start gap-3 text-sm cursor-pointer text-n-slate-12"
      >
        <span class="mt-0.5 shrink-0">
          <Checkbox
            :model-value="state.ticked"
            data-waslo-ai-confirm-tick
            @update:model-value="controller.setTicked"
          />
        </span>
        <span>{{ tick }}</span>
      </label>
    </div>
  </Dialog>
</template>
